import { useCallback, useEffect, useState } from 'react'
import { secondFactorApi } from 'core/api/secondFactor'
import { useSecondFactorFlow } from 'core/hooks/useSecondFactorFlow'
import { SecondFactorMethod, SecondFactorStatus } from 'core/models/SecondFactor'

export interface UseTwoFactorSettingsParams {
    isEmailConfirmed: boolean
    legacyTwoFactorAuth?: 'on' | 'off'
    legacyTotpEnabled?: boolean
}

const fromLegacy = (twoFactorAuth: 'on' | 'off' | undefined, totpEnabled: boolean | undefined): SecondFactorStatus => {
    if (totpEnabled) {
        return { method: 'totp', authenticatorBound: true, recoveryCodesRemaining: null }
    }
    if (twoFactorAuth === 'on') {
        return { method: 'email', authenticatorBound: false, recoveryCodesRemaining: null }
    }
    return { method: 'off', authenticatorBound: false, recoveryCodesRemaining: null }
}

export const useTwoFactorSettings = ({
    isEmailConfirmed,
    legacyTwoFactorAuth = 'off',
    legacyTotpEnabled = false,
}: UseTwoFactorSettingsParams) => {
    const [status, setStatus] = useState<SecondFactorStatus>(() => fromLegacy(legacyTwoFactorAuth, legacyTotpEnabled))
    const [methodsUnlocked, setMethodsUnlocked] = useState(false)

    const flow = useSecondFactorFlow({
        onStatus: setStatus,
    })
    const { start, isBusy, isOpen } = flow

    const refreshStatus = useCallback(() => {
        secondFactorApi
            .getSecondFactor()
            .then((next) => {
                if (!next) {
                    return
                }
                setStatus(next)
            })
            .catch(() => undefined)
    }, [])

    useEffect(() => {
        refreshStatus()
    }, [refreshStatus])

    useEffect(() => {
        if (status.method !== 'off') {
            setMethodsUnlocked(false)
        }
    }, [status.method])

    const selectMethod = useCallback(
        (method: SecondFactorMethod) => {
            if (isBusy || isOpen) {
                return
            }
            if (method === 'email' && !isEmailConfirmed) {
                return
            }
            if (method === status.method && method !== 'totp') {
                return
            }
            start({ action: 'select', method })
        },
        [isBusy, isOpen, isEmailConfirmed, start, status.method]
    )

    const setEnabled = useCallback(
        (nextEnabled: boolean) => {
            if (isBusy || isOpen) {
                return
            }
            if (!nextEnabled) {
                setMethodsUnlocked(false)
                if (status.method !== 'off') {
                    start({ action: 'select', method: 'off' })
                }
                return
            }
            if (status.method !== 'off') {
                return
            }
            if (isEmailConfirmed) {
                start({ action: 'select', method: 'email' })
                return
            }
            setMethodsUnlocked(true)
        },
        [isBusy, isOpen, isEmailConfirmed, start, status.method]
    )

    const unbindTotp = useCallback(() => {
        if (isBusy || isOpen) {
            return
        }
        start({
            action: 'select',
            method: isEmailConfirmed ? 'email' : 'off',
            intent: 'unbind_totp',
        })
    }, [isBusy, isOpen, isEmailConfirmed, start])

    const regenerateRecoveryCodes = useCallback(() => {
        if (!status.authenticatorBound || isBusy || isOpen) {
            return
        }
        start({ action: 'regenerate' })
    }, [isBusy, isOpen, start, status.authenticatorBound])

    return {
        status,
        enabled: status.method !== 'off' || methodsUnlocked,
        selectMethod,
        setEnabled,
        unbindTotp,
        regenerateRecoveryCodes,
        flow,
    }
}
